/**
 * Tenant Edit — Inline editing form for tenant details.
 * @see Documentation/Specification/020_Definitions/application-agnostic/identity/Tenant.md
 */
import { useState, useEffect } from 'react';
import { useGetOne, useUpdate, useNavigate, useNotify } from 'react-admin';
import {
  Box, Typography, Paper, Button, CircularProgress, Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { TenantRecord } from './types';

const useTenantId = () => window.location.pathname.split('/tenants/')[1]?.split('/')[0] ?? '';

const inputSx = { width: '100%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: 14 };

export const TenantEdit = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [update] = useUpdate();
  const id = useTenantId();
  const { data: tenant, isLoading } = useGetOne('tenants', { id });
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [registeredName, setRegisteredName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [currency, setCurrency] = useState('');
  const [timezone, setTimezone] = useState('');
  const [country, setCountry] = useState('');
  const [dirty, setDirty] = useState(false);

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && tenant) {
      const t = tenant as unknown as TenantRecord;
      setName(t.name);
      setRegisteredName(t.registeredName ?? '');
      setRegistrationNumber(t.registrationNumber ?? '');
      setEmail(t.contact?.email ?? '');
      setPhone(t.contact?.phone ?? '');
      setAddress(t.contact?.address ?? '');
      setCurrency(t.settings?.currency ?? 'ZAR');
      setTimezone(t.settings?.timezone ?? '');
      setCountry(t.settings?.country ?? '');
      setInitialized(true);
    }
  }, [tenant, initialized]);

  if (isLoading || !tenant) {
    return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  }

  const t = tenant as unknown as TenantRecord;
  const markDirty = () => { if (!dirty) setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await update('tenants', {
        id: t.id,
        data: {
          name,
          registeredName: registeredName || undefined,
          registrationNumber: registrationNumber || undefined,
          contact: { email, phone, address: address || undefined },
          settings: { currency, timezone, dateFormat: t.settings?.dateFormat, country },
        },
      });
      notify('Tenant updated', { type: 'success' });
      navigate(`/tenants/${t.id}/show`);
    } catch (err: any) {
      notify(err?.message ?? 'Failed to update tenant', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 700, mx: 'auto' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(`/tenants/${t.id}/show`)} sx={{ mb: 2 }}>
        Back to Tenant
      </Button>
      <Typography variant="h5" fontWeight={600} mb={3}>Edit Tenant</Typography>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Basic Info</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Name</Typography>
            <Box component="input" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setName(e.target.value); }} sx={inputSx} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Registered Name</Typography>
            <Box component="input" value={registeredName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setRegisteredName(e.target.value); }} sx={inputSx} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Registration Number</Typography>
            <Box component="input" value={registrationNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setRegistrationNumber(e.target.value); }} sx={inputSx} />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} mb={2}>Contact</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Box component="input" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setEmail(e.target.value); }} sx={inputSx} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Phone</Typography>
            <Box component="input" value={phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setPhone(e.target.value); }} sx={inputSx} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Address</Typography>
            <Box component="input" value={address} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setAddress(e.target.value); }} sx={inputSx} />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} mb={2}>Settings</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Currency</Typography>
            <Box component="input" value={currency} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setCurrency(e.target.value); }} sx={inputSx} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Timezone</Typography>
            <Box component="input" value={timezone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setTimezone(e.target.value); }} sx={inputSx} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Country</Typography>
            <Box component="input" value={country} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { markDirty(); setCountry(e.target.value); }} sx={inputSx} />
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={() => navigate(`/tenants/${t.id}/show`)}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};
